import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type LopPrunePercentageTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { useIsRTL } from "@/hooks";

import { Input2, Slider } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface LoppingPruningFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function LoppingPruningField<
  T extends
    LopPrunePercentageTreePropertyDescriptor = LopPrunePercentageTreePropertyDescriptor,
>({ onChange, treeDescriptor }: LoppingPruningFieldProps) {
  const isRTL = useIsRTL();

  const { isValid, messages } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.lop_prune_percentage,
  });

  const lopPrunePercentage = treeDescriptor.properties.get(
    TreePropertyType.lop_prune_percentage,
  ) as T | undefined;

  const value = lopPrunePercentage?.value
    ? parseFloat(lopPrunePercentage.value)
    : 0;

  const handleChange = () => (updater: (draft: T) => void) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.lop_prune_percentage,
      updater,
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel isValid={isValid}>
        {getText("Amount you're proposing to prune")}
      </FieldLabel>

      <div className="flex items-center gap-2">
        <Slider
          defaultValue={[0.1]}
          max={0.5}
          min={0.1}
          step={0.05}
          onValueChange={(value) => {
            handleChange()((draft) => {
              draft.value = value[0].toString();
            });
          }}
          value={[value]}
        />

        <Input2
          className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
          {...(isRTL
            ? {
                startClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                startContent: "%",
              }
            : {
                endClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                endContent: "%",
              })}
          max={50}
          min={10}
          step={5}
          onChange={(event) => {
            handleChange()((draft) => {
              const parsedValue = parseInt(event.target.value);
              draft.value = (parsedValue / 100).toFixed(2);
            });
          }}
          rounded="sm"
          type="number"
          value={(value * 100).toFixed(0)}
        />
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
